import * as _Footer from '#~/common/editor/footer/index.js';
import { Credentials as _Credentials, Caveat } from '#~/common/editor/index.js';
import * as Dialog from '@studio/design-system/dialog';
import { Icon } from '@studio/design-system/icon';
import type { Credentials as M_Credentials } from '@studio/models/auth';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { type Props as AddSourceAuthProps } from './types.js';
import { Spinner } from '@studio/design-system/spinner';
import { Sel, STag } from '#~/stag/tag.js';

/** `AddSourceAuth` component. */
export const AddSourceAuth = forwardRef<Dialog.Api, AddSourceAuthProps>(
  (
    {
      open,
      onOpenChange,
      onDismiss,
      onConfirm,
      error,
      connecting,
      rawPending,
      lockoutStatus,
      onLockoutChange
    },
    forwardedRef
  ): ReactElement => {
    const [authCredentials, setAuthCredentials] = useState<M_Credentials>();
    const [remainingTime, setRemainingTime] = useState<number>();

    const handleLockoutChange = useLatestFn((status: typeof lockoutStatus) => {
      onLockoutChange?.(status ?? null);
    });

    const handleConfirm = useLatestFn(() => {
      if (authCredentials?.username && authCredentials?.password) {
        onConfirm(authCredentials);
      }
    });

    useEffect(() => {
      if (!lockoutStatus?.isLocked || remainingTime === undefined) return;

      const timer = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev === undefined || prev <= 0) {
            clearInterval(timer);
            handleLockoutChange(null);
            return undefined;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }, [lockoutStatus?.isLocked, remainingTime, handleLockoutChange]);

    useEffect(() => {
      if (lockoutStatus?.isLocked) {
        setRemainingTime(lockoutStatus.unlockTimeSeconds);
      }
    }, [lockoutStatus]);

    return (
      <Dialog.Dialog open={open} onOpenChange={onOpenChange} ref={forwardedRef}>
        <Dialog.Overlay>
          <Dialog.Content className='fixed left-1/2 top-1/2 flex min-h-[500px] w-[506px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-12 bg-base-200 shadow-lg'>
            <Dialog.Header title='Authenticate Source' className='mb-20' />
            <div className='flex flex-grow flex-col gap-20 px-40 py-20'>
              <Caveat
                title='These are credentials you use to access your device, such as when you access the local IP web interface for your device.'
                className='mx-auto w-full max-w-[348px]'
              />
              <div className='space-y-24'>
                <_Credentials
                  credentials={toAccessor(authCredentials, setAuthCredentials)}
                  className={lockoutStatus?.isLocked ? 'pointer-events-none opacity-50' : ''}
                />
                {error &&
                  !lockoutStatus?.isLocked && (
                    // Tagged so hardware tests can wait on a rejected password's
                    // terminal state without a styling selector.
                    <div
                      className='flex items-center gap-8 rounded-8 bg-error/10 px-12 py-8 text-error'
                      {...STag(Sel.AddVideoDeviceDiscovery, 'auth-error')}
                    >
                      <Icon className='text-md' icon='alert-circle' />
                      <span className='text-sm'>{error}</span>
                    </div>
                  )}
                {lockoutStatus?.isLocked && (
                  <div className='flex items-center gap-8 rounded-8 bg-error/10 px-12 py-8 text-error'>
                    <Icon className='text-md' icon='alert-circle' />
                    <span className='text-sm'>
                      The device is now locked. Please wait {remainingTime} seconds before trying
                      again.
                    </span>
                  </div>
                )}
                {/* Suppress the in-progress spinner once an error is present: the
                    terminal, named failure state (the error banner above) must win
                    over an indefinite spinner so the operator sees a recoverable
                    dialog, not a stuck one. */}
                {connecting && !error && (
                  <div className='flex items-center gap-8 rounded-8 bg-primary/10 px-12 py-8 text-primary'>
                    <Spinner className='h-24 w-24' />
                    <span className='text-sm'>{connecting}</span>
                  </div>
                )}
                {rawPending && !connecting && (
                  <div className='text-base-content/70 text-sm'>
                    The previous connection request is still finishing.
                  </div>
                )}
              </div>
            </div>
            <Dialog.Footer>
              <_Footer.Back label='Cancel' onClick={onDismiss} />
              <_Footer.Next
                // Tagged so hardware tests submit without a role/label selector.
                // Per rule: playwright-stag-selectors-only (ai.knowledge/platform).
                {...STag(Sel.AddVideoDeviceDiscovery, 'auth-submit')}
                label='Authenticate and Continue'
                disabled={
                  !authCredentials?.username ||
                  !authCredentials?.password ||
                  // A present error is a terminal, recoverable failure: it must
                  // re-enable "Authenticate and Continue" even if a `connecting`
                  // flag lingers, so a failed link is never a permanent lockout.
                  (!!connecting && !error) ||
                  // Display timeout clears connecting but the raw write may still
                  // be running; a second submit would race that mutation.
                  !!rawPending ||
                  lockoutStatus?.isLocked
                }
                onClick={handleConfirm}
              />
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Dialog>
    );
  }
);
